import React, { useRef, useState } from 'react'
import { Box, Chip, Typography } from '@mui/material'
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded'
import { MAX_PATIENT_FILE_BYTES, UploadedPatientFile, useUploadPatientFile } from '../../../api/useUploadPatientFile'
import { useIsPanelPreview } from '../../../dashboard/panelPreviewContext'
import { advancedColors } from '../../../theme'

type Props = {
  uid: string
  files: UploadedPatientFile[]
  onChange: (files: UploadedPatientFile[]) => void
}

// Uploads on drop, not on submit, so `Add` only ever attaches already-uploaded `fileID`s.
const FileUploadField = ({ uid, files, onChange }: Props) => {
  const preview = useIsPanelPreview()
  const upload = useUploadPatientFile(uid)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const input = useRef<HTMLInputElement | null>(null)

  const addFiles = async (fileList: FileList | null) => {
    if (preview || !fileList || fileList.length === 0) {
      return
    }
    setError(null)
    setUploading(true)
    try {
      const uploaded = await Promise.all(Array.from(fileList).map((file) => upload(file)))
      onChange([...files, ...uploaded])
    } catch (uploadError) {
      setError(uploadError instanceof Error ? uploadError.message : 'Upload failed.')
    } finally {
      setUploading(false)
      if (input.current) input.current.value = ''
    }
  }

  return (
    <Box
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault()
        addFiles(event.dataTransfer.files).catch(() => undefined)
      }}
      onClick={() => {
        if (!preview) {
          input.current?.click()
        }
      }}
      sx={{
        border: `1px dashed ${advancedColors.lineStrong}`,
        borderRadius: 2,
        p: 2,
        cursor: preview ? 'default' : 'pointer',
        backgroundColor: advancedColors.surface,
      }}
    >
      <input
        ref={input}
        type="file"
        accept=".jpeg,.jpg,.png,.pdf"
        multiple
        hidden
        onChange={(event) => addFiles(event.target.files)}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: advancedColors.inkMuted }}>
        <UploadFileRoundedIcon fontSize="small" />
        <Typography variant="body2">Drag &apos;n&apos; drop files here, or click to select.</Typography>
      </Box>
      <Typography variant="body2" sx={{ color: advancedColors.inkFaint, mt: 0.5 }}>
        Supported file types: jpeg, jpg, png, pdf. Max file size: {MAX_PATIENT_FILE_BYTES / (1024 * 1024)} MB.
      </Typography>

      {uploading && (
        <Typography variant="body2" sx={{ mt: 1, color: advancedColors.inkMuted }}>
          Uploading…
        </Typography>
      )}
      {error && (
        <Typography variant="body2" sx={{ mt: 1, color: advancedColors.error }}>
          {error}
        </Typography>
      )}
      {files.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1.5 }} onClick={(event) => event.stopPropagation()}>
          {files.map((file) => (
            <Chip
              key={file.fileID}
              size="small"
              label={file.filename}
              onDelete={() => onChange(files.filter((candidate) => candidate.fileID !== file.fileID))}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

export default FileUploadField
